import React from 'react';
import styled from 'astroturf/react';

import type { StatStyle } from './arms';
import { formatCount } from './formatCount';

export const STACKED_TILE_WIDTH = 68;

const TileRoot = styled.a<{ loading?: boolean }>`
  @import 'stylesheets/design_system/font';
  @include sans-serif;

  display: flex;
  flex: 0 0 auto;
  box-sizing: border-box;
  align-items: center;
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:focus,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }

  /* The anchor fills its cell so the whole tile region is clickable; the
     content wrapper below keeps the rendered pixels where they were. Width
     hugs the content — only skeletons reserve space (SKELETON_WIDTHS). */
  align-self: stretch;

  &.loading {
    min-width: var(--skeleton-width, auto);
    cursor: default;
  }
`;

const TileContent = styled.span<{ statStyle?: StatStyle }>`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;

  &.statStyle-delta_chip {
    align-items: flex-end;
    column-gap: 14px;
  }
`;

const StatGroup = styled.span<{ statStyle?: StatStyle }>`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 6px;

  &.statStyle-delta_chip {
    row-gap: 3px;
  }
`;

const CountRow = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const Count = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono;

  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

const CountShimmer = styled.span`
  @keyframes impact-stat-count-shimmer {
    0%,
    100% {
      opacity: 0.5;
    }

    50% {
      opacity: 0.2;
    }
  }

  display: inline-block;
  width: 34px;
  height: 16px;
  border-radius: var(--border-radius-radius-sm, 4px);
  background: var(--brand-colors-neutral-300, #e3e3e8);
  animation: impact-stat-count-shimmer 1.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DotAnchor = styled.span`
  position: absolute;
  top: -2.5px;
  right: -9px;
  width: 8px;
  height: 8px;
  margin-left: 4px;
  border-radius: 50%;
  background: var(--error-dark, #b60000);
`;

const AdornmentSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const Label = styled.span`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: var(--text-primary, #141413);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export type ImpactStatTileProps = {
  count: number | null;
  label: string;
  statStyle?: StatStyle;
  href: string;
  adornment?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  showDot?: boolean;
  loading?: boolean;
  className?: string;
};

const ImpactStatTile = ({
  count,
  label,
  statStyle,
  adornment,
  href,
  onClick,
  showDot = true,
  loading = false,
  className,
}: ImpactStatTileProps) => {
  if (count == null && !loading) {
    return null;
  }

  return (
    <TileRoot
      data-testid="impact-stat-tile"
      loading={count == null}
      href={count == null ? undefined : href}
      onClick={count == null ? undefined : onClick}
      className={className}
    >
      <TileContent statStyle={statStyle}>
        <StatGroup statStyle={statStyle}>
          <CountRow>
            {count == null ? (
              <CountShimmer data-testid="impact-stat-count-shimmer" />
            ) : (
              <Count data-testid="impact-stat-count">
                {formatCount(count)}
              </Count>
            )}
            {count != null && showDot && (
              <DotAnchor data-testid="impact-stat-dot" />
            )}
          </CountRow>
          <Label>{label}</Label>
        </StatGroup>
        {count != null && adornment != null && (
          <AdornmentSlot data-testid="impact-stat-adornment">
            {adornment}
          </AdornmentSlot>
        )}
      </TileContent>
    </TileRoot>
  );
};

export default ImpactStatTile;
